import { useEffect, useRef } from 'react';
import fayeClient from '../services/fayeClient';

/**
 * React hook for subscribing to Faye channels
 * Automatically handles subscription/unsubscription on mount/unmount
 *
 * @param {string} channel - Faye channel to subscribe to (e.g., '/card', '/scrumboard')
 * @param {function} callback - Callback function to handle incoming messages
 * @param {array} deps - Optional dependency array (like useEffect deps)
 *
 * @example
 * useFayeSubscription('/card/updates', (message) => {
 *   console.log('Card updated:', message);
 *   // Update local state based on message
 * });
 */
export function useFayeSubscription(channel, callback, deps = []) {
  // Use ref to store the callback so we don't re-subscribe on every render
  const callbackRef = useRef(callback);

  // Update the ref when callback changes
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    if (!channel || !callbackRef.current) return;

    // Wrapper function that calls the latest callback
    const wrappedCallback = (message) => {
      if (callbackRef.current) {
        callbackRef.current(message);
      }
    };

    // Subscribe to channel
    fayeClient.subscribe(channel, wrappedCallback);

    console.log(`[useFayeSubscription] Subscribed to channel: ${channel}`);

    // Cleanup: unsubscribe when component unmounts or channel changes
    return () => {
      fayeClient.unsubscribe(channel, wrappedCallback);
      console.log(`[useFayeSubscription] Unsubscribed from channel: ${channel}`);
    };
  }, [channel, ...deps]);
}

export default useFayeSubscription;
